import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X, CheckCircle2, ShieldOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import type {
  CookiePrefsSource,
  OpenCookiePrefsDetail,
} from '@/lib/cookie-prefs';

/**
 * GDPR/CCPA-friendly cookie consent banner.
 *
 * Behavior:
 *   • Shows once per visitor until they choose Accept or Reject.
 *   • Choice is persisted to localStorage under "cookie-consent-v1".
 *   • Updates Google Consent Mode v2 via window.gtag('consent','update', …).
 *   • Rejecting cookies has NO impact on browsing or purchasing — purely
 *     disables analytics/ads cookies. Functional + security storage stay on
 *     so checkout, auth, and CSRF protection keep working.
 *   • Re-openable later via window.dispatchEvent(new Event('open-cookie-prefs')).
 */

const STORAGE_KEY = 'cookie-consent-v1';

type ConsentChoice = {
  analytics: boolean;
  decided_at: string;
  version: 1;
};

function readStored(): ConsentChoice | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.analytics === 'boolean') return parsed as ConsentChoice;
  } catch {
    /* ignore */
  }
  return null;
}

function applyConsent(analytics: boolean) {
  const w = window as any;
  if (typeof w.gtag === 'function') {
    w.gtag('consent', 'update', {
      ad_storage: analytics ? 'granted' : 'denied',
      ad_user_data: analytics ? 'granted' : 'denied',
      ad_personalization: analytics ? 'granted' : 'denied',
      analytics_storage: analytics ? 'granted' : 'denied',
    });
  }
}

/**
 * Returns a coarse device/browser fingerprint suitable for an audit log.
 * Intentionally lossy — never returns the full user-agent string and never
 * includes versions, OS details, or anything that could fingerprint a user.
 */
function classifyEnvironment(): {
  device_class: 'mobile' | 'tablet' | 'desktop' | 'unknown';
  browser_family: 'chrome' | 'firefox' | 'safari' | 'edge' | 'other' | 'unknown';
} {
  if (typeof navigator === 'undefined') {
    return { device_class: 'unknown', browser_family: 'unknown' };
  }
  const ua = navigator.userAgent || '';
  const lower = ua.toLowerCase();

  let device_class: 'mobile' | 'tablet' | 'desktop' | 'unknown' = 'desktop';
  if (/ipad|tablet/.test(lower)) device_class = 'tablet';
  else if (/mobi|iphone|android.+mobile/.test(lower)) device_class = 'mobile';

  let browser_family: 'chrome' | 'firefox' | 'safari' | 'edge' | 'other' | 'unknown' = 'other';
  if (/edg\//.test(lower)) browser_family = 'edge';
  else if (/chrome\//.test(lower) && !/edg\//.test(lower)) browser_family = 'chrome';
  else if (/firefox\//.test(lower)) browser_family = 'firefox';
  else if (/safari\//.test(lower) && !/chrome\//.test(lower)) browser_family = 'safari';

  return { device_class, browser_family };
}

/**
 * Best-effort, fire-and-forget audit log write. Never blocks the UI and
 * never fails the consent decision — analytics opt-out should always succeed
 * even if the network is offline.
 *
 * NOTE: The `cookie_consent_log` table is designed to contain ZERO PII —
 * no user_id, no IP, no email, no full UA string. Hence the `INSERT WITH
 * CHECK (true)` policy on the table is safe by construction.
 */
async function logConsentDecision(
  analytics: boolean,
  source: 'banner' | 'preferences' | 'footer-link',
) {
  try {
    const env = classifyEnvironment();
    await supabase.from('cookie_consent_log').insert({
      analytics_accepted: analytics,
      device_class: env.device_class,
      browser_family: env.browser_family,
      source,
    });
  } catch {
    /* swallow — audit log is best-effort and must never block the user */
  }
}

export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [currentChoice, setCurrentChoice] = useState<ConsentChoice | null>(null);
  // Tracks the trigger source (banner vs. which "open preferences" entry
  // point) so the audit log row is labelled correctly. Set from the
  // `open-cookie-prefs` CustomEvent's detail.source when present, else
  // falls back to the first-visit banner ('banner').
  const sourceRef = useRef<CookiePrefsSource>('banner');
  const dialogRef = useRef<HTMLDivElement>(null);
  const acceptBtnRef = useRef<HTMLButtonElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const stored = readStored();
    setCurrentChoice(stored);
    if (stored) {
      // Re-apply saved consent on every page load so Google Consent Mode
      // (and any other listeners) reflect the persisted choice across sessions.
      applyConsent(stored.analytics);
    } else {
      setVisible(true);
    }
    // Allow other UI (e.g. footer link) to re-open the banner.
    const reopen = (e: Event) => {
      setCurrentChoice(readStored());
      const detail = (e as CustomEvent<OpenCookiePrefsDetail>).detail;
      sourceRef.current = detail?.source ?? 'preferences';
      setVisible(true);
    };
    window.addEventListener('open-cookie-prefs', reopen);

    // Keep the banner state in sync if the user changes their choice in
    // another tab/window (storage events fire only in *other* tabs).
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      const next = readStored();
      setCurrentChoice(next);
      if (next) applyConsent(next.analytics);
    };
    window.addEventListener('storage', onStorage);

    return () => {
      window.removeEventListener('open-cookie-prefs', reopen);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  // Focus management: when the dialog opens, remember the currently-focused
  // element, move focus to the primary action, and trap Tab inside the dialog.
  // ESC closes the dialog (treated as "no change" — keeps stored choice).
  useEffect(() => {
    if (!visible) return;

    previouslyFocusedRef.current =
      (document.activeElement as HTMLElement | null) ?? null;

    // Defer focus to next frame so the dialog is mounted before focusing.
    const focusTimer = window.setTimeout(() => {
      acceptBtnRef.current?.focus();
    }, 0);

    function getFocusable(): HTMLElement[] {
      const root = dialogRef.current;
      if (!root) return [];
      const sel =
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';
      return Array.from(root.querySelectorAll<HTMLElement>(sel)).filter(
        (el) => !el.hasAttribute('aria-hidden') && el.offsetParent !== null,
      );
    }

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        // ESC closes without changing the stored choice. If no stored choice
        // exists yet, treat as "decide later" — leave banner queued for next
        // page load by simply hiding for this session.
        setVisible(false);
        return;
      }
      if (e.key !== 'Tab') return;
      const focusable = getFocusable();
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey) {
        if (active === first || !dialogRef.current?.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', onKeyDown);
      // Return focus to whatever opened the dialog. If that element has
      // been unmounted (e.g. user navigated routes while the dialog was
      // open) fall back to the page's skip-to-content link or <main> so
      // keyboard/SR users always land on a predictable, visible target
      // instead of <body>.
      const prev = previouslyFocusedRef.current;
      if (prev && typeof prev.focus === 'function' && document.contains(prev)) {
        prev.focus();
      } else {
        const fallback =
          (document.querySelector('[data-testid="skip-to-content"]') as HTMLElement | null) ??
          (document.getElementById('main-content') as HTMLElement | null);
        if (fallback) {
          if (!fallback.hasAttribute('tabindex')) {
            fallback.setAttribute('tabindex', '-1');
          }
          fallback.focus({ preventScroll: true });
        }
      }
    };
  }, [visible]);

  function decide(analytics: boolean) {
    const choice: ConsentChoice = {
      analytics,
      decided_at: new Date().toISOString(),
      version: 1,
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(choice));
    } catch {
      /* localStorage unavailable — choice still applied for this page */
    }
    applyConsent(analytics);
    // Fire-and-forget: append a privacy-safe audit row. No PII is sent.
    // Telemetry source comes from the event that opened the dialog when
    // available; the audit log accepts a small enum of known sources.
    const auditSource: 'banner' | 'preferences' | 'footer-link' =
      sourceRef.current === 'banner' ? 'banner'
      : sourceRef.current === 'footer-link' ? 'footer-link'
      : 'preferences';
    void logConsentDecision(analytics, auditSource);
    setCurrentChoice(choice);
    setVisible(false);
    sourceRef.current = 'banner';
  }

  if (!visible) return null;

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-prefs-title"
      aria-describedby="cookie-prefs-desc"
      className="fixed inset-x-3 bottom-3 z-[9998] sm:inset-x-auto sm:left-4 sm:bottom-4 sm:max-w-md"
    >
      <div className="rounded-xl border border-border bg-card/95 backdrop-blur-md shadow-2xl p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <Cookie className="h-5 w-5 text-primary mt-0.5 shrink-0" aria-hidden />
          <div className="flex-1 min-w-0">
            <h2 id="cookie-prefs-title" className="text-sm font-semibold text-foreground">
              Cookies (your choice)
            </h2>
            {currentChoice && (
              <div
                className={`mt-2 flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs ${
                  currentChoice.analytics
                    ? 'border-primary/30 bg-primary/5 text-foreground'
                    : 'border-muted bg-muted/40 text-foreground'
                }`}
                aria-live="polite"
                role="status"
              >
                {currentChoice.analytics ? (
                  <CheckCircle2 className="h-3.5 w-3.5 text-primary shrink-0" aria-hidden />
                ) : (
                  <ShieldOff className="h-3.5 w-3.5 text-muted-foreground shrink-0" aria-hidden />
                )}
                <span className="min-w-0 flex-1">
                  Current setting:{' '}
                  <strong className="font-semibold">
                    {currentChoice.analytics ? 'Optional cookies accepted' : 'Optional cookies rejected'}
                  </strong>
                  <span className="text-muted-foreground">
                    {' '}· saved {new Date(currentChoice.decided_at).toLocaleDateString()}
                  </span>
                </span>
              </div>
            )}
            <p id="cookie-prefs-desc" className="text-xs text-muted-foreground mt-1 leading-relaxed">
              We use a small number of essential cookies to make checkout and sign-in
              work. Optional analytics cookies help us improve the product. You can
              <strong> reject all optional cookies</strong> — it will <strong>not</strong> affect your
              ability to browse, run a SLAW Quick Check, or purchase a report.
            </p>
            <div
              className="mt-3 flex flex-wrap items-center gap-2"
              role="group"
              aria-label="Cookie preference choices"
            >
              <Button
                ref={acceptBtnRef}
                size="sm"
                onClick={() => decide(true)}
                className="h-8"
                variant={currentChoice?.analytics === true ? 'default' : 'default'}
                aria-pressed={currentChoice?.analytics === true}
                aria-label={
                  currentChoice?.analytics === true
                    ? 'Accept all optional cookies (currently selected)'
                    : 'Accept all optional cookies'
                }
              >
                {currentChoice?.analytics === true && (
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1" aria-hidden />
                )}
                Accept all
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => decide(false)}
                className="h-8"
                aria-pressed={currentChoice?.analytics === false}
                aria-label={
                  currentChoice?.analytics === false
                    ? 'Reject optional cookies (currently selected)'
                    : 'Reject optional cookies'
                }
              >
                {currentChoice?.analytics === false && (
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1 text-primary" aria-hidden />
                )}
                Reject optional
              </Button>
              <Link
                to="/privacy-policy"
                className="text-[11px] text-muted-foreground underline underline-offset-2 hover:text-foreground ml-auto"
                aria-label="Learn more about our cookie policy (opens privacy policy)"
              >
                Learn more
              </Link>
            </div>
          </div>
          <button
            type="button"
            onClick={() => decide(false)}
            aria-label="Close cookie preferences and reject optional cookies"
            className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 transition-colors shrink-0"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </div>
    </div>
  );
}
